import {
  AccountBalance,
  CheckOutlined,
  ClearOutlined,
  ScreenLockPortrait,
} from "@mui/icons-material";
import { Box, useTheme } from "@mui/material";
import type { StepIconProps } from "@mui/material";
import type { Theme } from "@mui/material/styles";
import type { SvgIconProps } from "@mui/material/SvgIcon";
import { Colors } from "Components/Layout/Themes/Colors";
import { MooneIcon } from "Components/Shared/Icons/MooneIcon";
import { useResponsiveValue } from "Hooks/useResponsiveValue";
import type { PaymentStep } from "Components/AnonymousCustomer/Payment/config";
import type { ComponentType } from "react";

const getActiveStepColor = (palette: Theme["palette"], step: PaymentStep) => {
  const colorByStep: Record<PaymentStep, string> = {
    detail: palette.primary.main,
    bank: Colors.Purple,
    account: palette.warning.main,
  };

  return colorByStep[step];
};

const ICONS: Record<PaymentStep, ComponentType<SvgIconProps>> = {
  detail: MooneIcon,
  bank: AccountBalance,
  account: ScreenLockPortrait,
};

interface Props extends StepIconProps {
  hasError?: boolean;
  step: PaymentStep;
}

export const AnonymousPaymentStepIcon = ({
  active,
  completed,
  className,
  step,
  hasError,
}: Props) => {
  const getResponsiveValue = useResponsiveValue();
  const { palette } = useTheme();

  const Icon =
    hasError && active
      ? ClearOutlined
      : completed
        ? CheckOutlined
        : (ICONS[step] ?? CheckOutlined);

  const color =
    active || completed ? palette.common.white : palette.text.disabled;

  const stepColor =
    hasError && active
      ? palette.error.main
      : completed
        ? palette.success.main
        : active
          ? getActiveStepColor(palette, step)
          : palette.grey[300];

  return (
    <Box
      aria-hidden
      className={className}
      sx={({ palette }) => ({
        width: [32, 48],
        height: [32, 48],
        borderRadius: "50%",
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        position: "relative",
        top: 0,
        color,
        bgcolor: completed
          ? palette.success.main
          : active
            ? stepColor
            : palette.grey[300],
        boxShadow: active ? `0 0 0 4px ${stepColor}33` : "none",
      })}
    >
      <Icon fontSize={getResponsiveValue(["small", "medium"])} />
    </Box>
  );
};
